import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { Mail, RefreshCw } from 'lucide-react'
import type { RowSelectionState } from '@tanstack/react-table'
import {
  BulkActionBar,
  Button,
  EmptyState,
  QueryErrorState,
  MetricCard,
  MetricStrip,
  PageHeaderBand,
  SearchInput,
  Select,
  Toolbar,
  ToolbarCount,
  ToolbarLeft,
  ToolbarRight,
} from '@/components/ui'
import {
  MAX_EMAIL_SOPS,
  SopEmailDrawer,
  SopTable,
  useSopCategories,
  useSops,
  type SopListQuery,
  type SopRow,
} from '@/shell/services/sop-registry'

/**
 * SOP Library — read-only reference surface for all internal roles.
 * Persona: any employee looking up "how do we do X" and downloading or
 * emailing the procedure to a colleague. No mutation affordances —
 * those live on the separate /sop-manage authoring page.
 */
export function SopLibraryPage() {
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [category, setCategory] = useState('')
  const navigate = useNavigate()
  const [selection, setSelection] = useState<RowSelectionState>({})
  const [emailOpen, setEmailOpen] = useState(false)
  // Titles of ticked SOPs, so they keep their names after a search hides them.
  const [titles, setTitles] = useState<Record<string, string>>({})
  const searchRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search), 200)
    return () => clearTimeout(id)
  }, [search])

  const query = useMemo<SopListQuery>(
    () => ({
      q: debouncedSearch || undefined,
      category: category || undefined,
      status: 'published',
      limit: 200,
    }),
    [debouncedSearch, category],
  )

  const { data, isLoading, isFetching, isError, error, refetch } = useSops(query)
  const { data: categoriesData } = useSopCategories()

  const rows = useMemo<SopRow[]>(() => data?.items ?? [], [data])
  // Ticking happens on visible rows, so the title is known at that moment.
  const changeSelection = (next: RowSelectionState) => {
    setSelection(next)
    setTitles((prev) => {
      const out = { ...prev }
      for (const r of rows) if (next[r.id]) out[r.id] = r.title
      return out
    })
  }
  const selectedIds = Object.keys(selection).filter((id) => selection[id])
  const selectedSops = selectedIds.map((id) => ({ id, title: titles[id] ?? 'SOP' }))
  const categories = categoriesData?.categories ?? []

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== '/') return
      if (e.target instanceof HTMLElement) {
        const typing =
          e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.isContentEditable
        if (typing) return
      }
      e.preventDefault()
      searchRef.current?.focus()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  return (
    <div data-density="comfortable" className="flex h-full flex-col">
      <PageHeaderBand
        title="SOP Library"
        description="Standard Operating Procedures across every department. Click a procedure to read it; tick several to email them together."
      >
        <MetricStrip variant="inline" className="ml-auto">
          <MetricCard variant="inline" label="Procedures" value={(data?.total ?? rows.length).toLocaleString()} />
        </MetricStrip>
      </PageHeaderBand>

      <div className="flex min-h-0 flex-1 flex-col">
        <Toolbar>
          <SearchInput
            ref={searchRef}
            containerClassName="flex-1 max-w-none"
            placeholder="Search SOP title, tags, or content…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search"
          />
        </Toolbar>
        <Toolbar>
          <ToolbarLeft>
            <Select aria-label="Category" value={category} onChange={(e) => setCategory(e.target.value)}>
              <option value="">All categories</option>
              {categories.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </Select>
          </ToolbarLeft>
          <ToolbarRight>
            <ToolbarCount>{rows.length.toLocaleString()} procedures</ToolbarCount>
            <Button
              variant="ghost"
              size="sm"
              iconOnly
              aria-label="Refresh"
              loading={isFetching && !isLoading}
              onClick={() => refetch()}
            >
              <RefreshCw size={14} />
            </Button>
          </ToolbarRight>
        </Toolbar>

        <div className="flex-1 min-h-0 overflow-auto bg-canvas p-4">
          {isError ? (
            <QueryErrorState
              error={error}
              onRetry={() => void refetch()}
              subject="SOPs"
            />
          ) : rows.length === 0 && !isLoading ? (
            <EmptyState
              variant="no-match"
              title="No SOPs match"
              description="Try clearing the search or category filter."
              action={
                <Button
                  variant="secondary"
                  onClick={() => {
                    setSearch('')
                    setCategory('')
                  }}
                >
                  Reset filters
                </Button>
              }
            />
          ) : (
            <SopTable
              rows={rows}
              onSelect={(row) => navigate({ to: '/sop-library/$sopId', params: { sopId: row.id } })}
              moduleId="sop-library"
              selection={selection}
              onSelectionChange={changeSelection}
            />
          )}
        </div>
      </div>


      <BulkActionBar count={selectedIds.length} onClear={() => setSelection({})}>
        <Button
          variant="primary"
          size="sm"
          disabled={selectedIds.length > MAX_EMAIL_SOPS}
          title={selectedIds.length > MAX_EMAIL_SOPS ? `Email up to ${MAX_EMAIL_SOPS} SOPs at a time` : undefined}
          onClick={() => setEmailOpen(true)}
        >
          <Mail size={14} /> Email selected
        </Button>
      </BulkActionBar>

      <SopEmailDrawer
        open={emailOpen}
        onOpenChange={setEmailOpen}
        sops={selectedSops}
        onSent={() => setSelection({})}
      />
    </div>
  )
}
